import api from '@/lib/api';

/**
 * Client for the super-admin storefront property content editor
 * (apps/api .../property-content.controller.ts — all routes under
 * /v1/admin/platform/properties). Types mirror the service DTO shapes returned
 * by PropertyContentService exactly. The whole surface is behind the
 * SUPER_ADMIN @PlatformRoute fence, so INTERNAL fields (KYC/WiFi creds/contacts)
 * are present and may be shown in the editor.
 */

// ─── Enums (mirror apps/api entity enums) ──────────────────────────────────────

export type UnitTypeValue = 'studio' | '1bed' | '2bed' | '3bed' | 'penthouse';
export const UNIT_TYPES: { value: UnitTypeValue; label: string }[] = [
  { value: 'studio', label: 'Studio' },
  { value: '1bed', label: '1 Bed' },
  { value: '2bed', label: '2 Bed' },
  { value: '3bed', label: '3 Bed' },
  { value: 'penthouse', label: 'Penthouse' },
];

export type AmenityKind = 'APARTMENT' | 'COMMUNITY' | 'CONFIG';
export type AmenityLevel = 'room' | 'building' | 'config';

export type MediaType = 'image' | 'video';
export type MediaScope = 'property' | 'unit';

export type ContactRole = 'housekeeping' | 'security' | 'emergency' | 'maintenance';
export const CONTACT_ROLES: { value: ContactRole; label: string }[] = [
  { value: 'housekeeping', label: 'Housekeeping' },
  { value: 'security', label: 'Security' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'maintenance', label: 'Maintenance' },
];

// ─── Response shapes (mirror PropertyContentService.getFull) ───────────────────

export interface PcPropertyCore {
  id: string;
  name: string;
  description: string | null;
  tagLine: string | null;
  address: string;
  city: string;
  state: string | null;
  country: string;
  zipCode: string | null;
  latitude: number | null;
  longitude: number | null;
  currency: string;
  website: string | null;
  isActive: boolean;
  reviewStatus: string;
  checkInTime: string;
  checkOutTime: string;
  cancellationPolicy: string;
  petPolicy: string;
  smokingPolicy: string;
  houseRules: string | null;
  updatedAt: string;
}

export interface PcTeamSeat {
  name: string;
  email: string;
  roleLabel: string;
}

export interface PcProfile {
  onsiteHours: string | null;
  unitsSummary: string | null;
  maxFloors: number | null;
  onsiteContactName: string | null;
  onsiteContactEmail: string | null;
  receptionPhone: string | null;
  managerPhone: string | null;
  outOfHoursPhone: string | null;
  managerFullName: string | null;
  managerDob: string | null;
  brandNameOnOtas: boolean | null;
  emergencyPhone: string | null;
  teamParking: string | null;
  maintenanceReporting: string | null;
  minGuestAge: number | null;
  minNightStay: number | null;
  childrenAllowed: boolean | null;
  childrenNotes: string | null;
  petFeeNotes: string | null;
  studentsOnly: boolean | null;
  minAdvanceBookingDays: number | null;
  checkInEndTime: string | null;
  checkinProcedure: string | null;
  checkoutProcedure: string | null;
  wifiSpeed: string | null;
  sharedFacilityAccess: string | null;
  fireEvacuation: string | null;
  rubbishRemoval: string | null;
  onsiteParking: string | null;
  nearestParking: string | null;
  heatingCooling: string | null;
  washingMachine: string | null;
  deliveriesLoading: string | null;
  faqOutOfHoursAccess: string | null;
  faqBedding: string | null;
  faqTowels: string | null;
  faqLuggageStorage: string | null;
  faqMail: string | null;
  faqBikeStorage: string | null;
  faqBuildingLockedHours: string | null;
  keyType: string | null;
  faqFans: string | null;
  buildingEntryHours: string | null;
  lostKeyFee: string | null;
  wifiSsid: string | null;
  wifiUsername: string | null;
  wifiPassword: string | null;
  wifiRouterLocation: string | null;
  lostKeyContact: string | null;
  masterKeyProtocol: string | null;
  afterHoursLockout: string | null;
  afterHoursEmergencyContact: string | null;
  teamSeats: PcTeamSeat[];
}

export interface PcContact {
  id: string;
  role: ContactRole;
  name: string | null;
  email: string | null;
  phone: string | null;
}

export interface PcKyc {
  registeredCompany: string | null;
  addressLine1: string | null;
  city: string | null;
  postcode: string | null;
  country: string | null;
  vatId: string | null;
}

export interface PcAmenityCatalogueItem {
  id: number;
  name: string;
  kind: AmenityKind;
}

export interface PcAmenitySelection {
  amenityId: number;
  selected: boolean;
  level: AmenityLevel | null;
  comment: string | null;
}

export interface PcMedia {
  id: string;
  type: MediaType;
  scope: MediaScope;
  unitId: string | null;
  url: string;
  caption: string | null;
  category: string | null;
  sortOrder: number;
  isCover: boolean;
}

export interface PcSoftFurnishing {
  id: string;
  item: string;
  scope: string;
  unitKind: string | null;
  quantity: number;
}

export interface PcUnit {
  id: string;
  name: string;
  type: UnitTypeValue;
  capacity: number;
  /** Guests included in the base rate; null ⇒ = capacity (no extra-person charge). */
  baseOccupancy: number | null;
  /** Hotel-set fee per extra guest per night over base occupancy (decimal string). */
  extraPersonCharge: string;
  basePrice: string | null;
  weekendPrice: string | null;
  minimumStay: number;
  isActive: boolean;
}

export interface PcPublishGate {
  isLive: boolean;
  pricedRoomCount: number;
  reasons: string[];
}

export interface PcPms {
  hasPmsIntegration: boolean;
  pmsSyncedAt: string | null;
}

export interface PropertyContentFull {
  property: PcPropertyCore;
  profile: PcProfile | null;
  contacts: PcContact[];
  kyc: PcKyc | null;
  amenities: {
    catalogue: PcAmenityCatalogueItem[];
    selected: PcAmenitySelection[];
  };
  media: PcMedia[];
  softFurnishing: PcSoftFurnishing[];
  units: PcUnit[];
  publishGate: PcPublishGate;
  pms: PcPms;
}

export interface PcSyncResult {
  syncedAt: string;
  amenitiesSynced: number;
  unitsCount: number;
}

const BASE = '/admin/platform/properties';

// ─── Reads ─────────────────────────────────────────────────────────────────

export async function fetchPropertyContentFull(id: string): Promise<PropertyContentFull> {
  const { data } = await api.get<PropertyContentFull>(`${BASE}/${id}/full`);
  return data;
}

export async function fetchAmenityCatalogue(): Promise<PcAmenityCatalogueItem[]> {
  const { data } = await api.get<PcAmenityCatalogueItem[]>(`${BASE}/amenities/catalogue`);
  return data;
}

// ─── Core ──────────────────────────────────────────────────────────────────

export interface UpdateCoreBody {
  name?: string | undefined;
  description?: string | undefined;
  tagLine?: string | undefined;
  address?: string | undefined;
  city?: string | undefined;
  state?: string | undefined;
  country?: string | undefined;
  zipCode?: string | undefined;
  latitude?: number | undefined;
  longitude?: number | undefined;
  currency?: string | undefined;
  website?: string | undefined;
  checkInTime?: string | undefined;
  checkOutTime?: string | undefined;
  cancellationPolicy?: string | undefined;
  petPolicy?: string | undefined;
  smokingPolicy?: string | undefined;
  houseRules?: string | undefined;
  isActive?: boolean | undefined;
}

export async function updatePropertyCore(
  id: string,
  body: UpdateCoreBody,
): Promise<PropertyContentFull> {
  const { data } = await api.patch<PropertyContentFull>(`${BASE}/${id}/core`, body);
  return data;
}

// ─── Profile ─────────────────────────────────────────────────────────────────

export type UpsertProfileBody = Partial<{
  onsiteHours: string;
  unitsSummary: string;
  maxFloors: number;
  onsiteContactName: string;
  onsiteContactEmail: string;
  receptionPhone: string;
  managerPhone: string;
  outOfHoursPhone: string;
  managerFullName: string;
  managerDob: string;
  brandNameOnOtas: boolean;
  emergencyPhone: string;
  teamParking: string;
  maintenanceReporting: string;
  minGuestAge: number;
  minNightStay: number;
  childrenAllowed: boolean;
  childrenNotes: string;
  petFeeNotes: string;
  studentsOnly: boolean;
  minAdvanceBookingDays: number;
  checkInEndTime: string;
  checkinProcedure: string;
  checkoutProcedure: string;
  wifiSpeed: string;
  sharedFacilityAccess: string;
  fireEvacuation: string;
  rubbishRemoval: string;
  onsiteParking: string;
  nearestParking: string;
  heatingCooling: string;
  washingMachine: string;
  deliveriesLoading: string;
  faqOutOfHoursAccess: string;
  faqBedding: string;
  faqTowels: string;
  faqLuggageStorage: string;
  faqMail: string;
  faqBikeStorage: string;
  faqBuildingLockedHours: string;
  keyType: string;
  faqFans: string;
  buildingEntryHours: string;
  lostKeyFee: number;
  wifiSsid: string;
  wifiUsername: string;
  wifiPassword: string;
  wifiRouterLocation: string;
  lostKeyContact: string;
  masterKeyProtocol: string;
  afterHoursLockout: string;
  afterHoursEmergencyContact: string;
  teamSeats: PcTeamSeat[];
}>;

export async function upsertProfile(id: string, body: UpsertProfileBody): Promise<PcProfile> {
  const { data } = await api.patch<PcProfile>(`${BASE}/${id}/profile`, body);
  return data;
}

// ─── Contacts ────────────────────────────────────────────────────────────────

export interface ContactInput {
  role: ContactRole;
  name?: string;
  email?: string;
  phone?: string;
}

export async function setContacts(id: string, contacts: ContactInput[]): Promise<PcContact[]> {
  const { data } = await api.put<PcContact[]>(`${BASE}/${id}/contacts`, { contacts });
  return data;
}

// ─── KYC ───────────────────────────────────────────────────────────────────

export type UpsertKycBody = Partial<{
  registeredCompany: string;
  addressLine1: string;
  city: string;
  postcode: string;
  country: string;
  vatId: string;
}>;

export async function upsertKyc(id: string, body: UpsertKycBody): Promise<PcKyc> {
  const { data } = await api.patch<PcKyc>(`${BASE}/${id}/kyc`, body);
  return data;
}

// ─── Amenities ───────────────────────────────────────────────────────────────

export interface AmenitySelectionInput {
  amenityId: number;
  selected: boolean;
  level?: AmenityLevel;
  comment?: string;
}

export async function setAmenities(
  id: string,
  selections: AmenitySelectionInput[],
): Promise<PcAmenitySelection[]> {
  const { data } = await api.put<PcAmenitySelection[]>(`${BASE}/${id}/amenities`, {
    selections,
  });
  return data;
}

// ─── Units ─────────────────────────────────────────────────────────────────

export interface CreateUnitBody {
  name: string;
  type: UnitTypeValue;
  capacity: number;
  baseOccupancy?: number | undefined;
  extraPersonCharge?: number | undefined;
  basePrice: number;
  weekendPrice?: number | undefined;
  minimumStay?: number | undefined;
  isActive?: boolean | undefined;
}

export type UpdateUnitBody = Partial<CreateUnitBody>;

export async function createUnit(id: string, body: CreateUnitBody): Promise<PcUnit> {
  const { data } = await api.post<PcUnit>(`${BASE}/${id}/units`, body);
  return data;
}

export async function updateUnit(
  id: string,
  unitId: string,
  body: UpdateUnitBody,
): Promise<PcUnit> {
  const { data } = await api.patch<PcUnit>(`${BASE}/${id}/units/${unitId}`, body);
  return data;
}

export async function deleteUnit(id: string, unitId: string): Promise<void> {
  await api.delete(`${BASE}/${id}/units/${unitId}`);
}

// ─── Media ─────────────────────────────────────────────────────────────────

export interface CreateMediaBody {
  type: MediaType;
  scope: MediaScope;
  unitId?: string | undefined;
  url: string;
  caption?: string | undefined;
  category?: string | undefined;
}

export async function createMedia(id: string, body: CreateMediaBody): Promise<PcMedia> {
  const { data } = await api.post<PcMedia>(`${BASE}/${id}/media`, body);
  return data;
}

export async function setMediaCategory(
  id: string,
  mediaId: string,
  category: string | null,
): Promise<PcMedia> {
  const { data } = await api.patch<PcMedia>(`${BASE}/${id}/media/${mediaId}/category`, {
    category,
  });
  return data;
}

export async function reorderMedia(id: string, orderedIds: string[]): Promise<PcMedia[]> {
  const { data } = await api.patch<PcMedia[]>(`${BASE}/${id}/media/reorder`, { orderedIds });
  return data;
}

export async function setMediaCover(id: string, mediaId: string): Promise<PcMedia> {
  const { data } = await api.patch<PcMedia>(`${BASE}/${id}/media/${mediaId}/cover`);
  return data;
}

export async function deleteMedia(id: string, mediaId: string): Promise<void> {
  await api.delete(`${BASE}/${id}/media/${mediaId}`);
}

// ─── Publish / unpublish / sync ────────────────────────────────────────────

export async function publishProperty(id: string): Promise<PropertyContentFull> {
  const { data } = await api.post<PropertyContentFull>(`${BASE}/${id}/publish`);
  return data;
}

export async function unpublishProperty(id: string): Promise<PropertyContentFull> {
  const { data } = await api.post<PropertyContentFull>(`${BASE}/${id}/unpublish`);
  return data;
}

export async function syncToPms(id: string, confirm: string): Promise<PcSyncResult> {
  const { data } = await api.post<PcSyncResult>(`${BASE}/${id}/sync-to-pms`, { confirm });
  return data;
}
